import React, { useState, useEffect, forwardRef, useImperativeHandle, useContext } from 'react';
import {
  TextField,
  Autocomplete,
  Checkbox,
  Typography,
  Grid,
  Stack,
  Card
} from '@mui/material';
import dayjs from 'dayjs';
import { getRequestConfig } from '../auth/auth';
import { ApiGateway } from '../config/config';
import axios from 'axios';
import RoleContext from '../auth/RoleContext';
import { ROLES } from '../auth/role';

const searchExtendedAttributeValues = async (entityType, entityId) => {
  const requestConfig = await getRequestConfig();
  const basePath = `${ApiGateway.attribute}/values/${entityType}/${entityId}`;
  const response = await axios.get(basePath, requestConfig);
  if (response.data) {
    return response.data;
  }
};

const getAttributeValueOptions = async (attributeDefinitionId) => {
  const requestConfig = await getRequestConfig();
  const basePath = `${ApiGateway.attribute}/values/options/${attributeDefinitionId}`;
  const response = await axios.get(basePath, requestConfig);
  if (response.data) {
    return response.data;
  }
};

const upsertAttributeValue = async (entityType, entityId, attributeId, value) => {
  const requestConfig = await getRequestConfig();
  const basePath = `${ApiGateway.attribute}/values/${entityType}/${entityId}/${attributeId}`;
  const response = await axios.put(basePath, { value }, requestConfig);
  if (response.data) {
    return response.data;
  }
}

const ExtendedAttributesSection = forwardRef(function ExtendedAttributesSection(
  { entityType, entityId, onSaved, editable = true, onChange = () => { } },
  ref
) {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState([])
  const [attributeOptions, setAttributeOptions] = useState()

  const roleContext = useContext(RoleContext)
  const canEditAttributes = roleContext.hasRole([ROLES.ADMIN, ROLES.MFI_ADMIN])
  const effectiveEditable = editable && canEditAttributes
  
  /** Fetch attributes on mount/prop change */
  useEffect(() => {
    let isMounted = true;
    const fetchAttributes = async () => {
      const data = await searchExtendedAttributeValues(entityType, entityId);
      if (isMounted) {
        setAttributes(data || []);
      }
    };
    if (entityType && entityId) {
      try {
        setLoading(true);
        fetchAttributes();
      } catch (err) {
        console.error('Error fetching extended attributes:', err);
        if (isMounted) setError(err);
      } finally {
        if (isMounted) setLoading(false);
      }

    }
    return () => {
      isMounted = false;
    };
  }, [entityType, entityId]);

  /** Fetch attribute options */
  useEffect(() => {
    const fetchAttributeOptions = async () => {
      const promises = attributes
        .filter(attr => attr.shareValues)
        .map(async attribute => {
          const options = await getAttributeValueOptions(attribute.id);
          return { ...attribute, options };
        });
      const data = await Promise.all(promises)
      const options = data.reduce((s, op) => {
        s[op.id] = op.options
        return s
      }, {})
      setAttributeOptions(options);
    }
    if (attributes?.length && !attributeOptions) fetchAttributeOptions()
  }, [attributes, attributeOptions]);

  /** Handle field changes */
  const handleChangeValue = (id, value) => {
    onChange(id, value)
    const _dirty = Array.from(new Set([...dirty, id]))
    setDirty(_dirty)
    setAttributes((prev) =>
      prev.map((attr) =>
        attr.id === id ? { ...attr, value } : attr
      )
    );
  };

  /** Save mechanics encapsulated inside the component */
  const saveAttributes = async () => {
    setSaving(true);
    try {
      // iterate over current attributes, call upsert for each one
      for (const attr of attributes) {
        if (dirty.includes(attr.id)) {
          await upsertAttributeValue(entityType, entityId, attr.id, attr.value);
        }
      }

      // Optional: Notify parent after save
      if (onSaved) {
        onSaved({ success: true, count: attributes.length });
      }
    } catch (err) {
      console.error('Error saving attributes:', err);
      if (onSaved) {
        onSaved({ success: false, error: err });
      }
    } finally {
      setSaving(false);
    }
  };

  const isValid = () => {
    return true
  }

  /** Expose save() method to parent via ref */
  useImperativeHandle(ref, () => ({
    save: saveAttributes,
    isValid,
    visible: attributes?.length > 0
  }));

  const renderPropertyCell = (attr) => {
    if (!attr) return null;

    let displayValue = attr.value;
    if (attr.dataType === 'boolean') {
      displayValue = attr.value === true || attr.value === 'true' ? 'Yes' : 'No';
    } else if (attr.dataType === 'date') {
      displayValue = attr.value ? dayjs(attr.value).format('YYYY-MM-DD') : '';
    } else if (attr.dataType === 'datetime') {
      displayValue = attr.value ? dayjs(attr.value).format('YYYY-MM-DD HH:mm') : '';
    } else if (attr.dataType === 'json') {
      displayValue = typeof attr.value === 'object'
        ? JSON.stringify(attr.value, null, 2)
        : attr.value;
    }

    return (
      <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
        <Grid container>
          {/* Label cell */}
          <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1 }}>
            <Typography fontWeight="bold" color="text.secondary" align="right">
              {attr.label}:
            </Typography>
          </Grid>

          {/* Value / Field cell */}
          <Grid item xs={9}>
            {!effectiveEditable ? (
              <Typography>{displayValue || '-'}</Typography>
            ) : (
              (() => {
                switch (attr.dataType) {
                  case 'boolean':
                    return (
                      <Checkbox
                        disabled={saving}
                        checked={attr.value === true || attr.value === 'true'}
                        onChange={(e) => handleChangeValue(attr.id, e.target.checked)}
                      />
                    );
                  case 'date':
                    return (
                      <TextField
                        disabled={saving}
                        type="date"
                        value={attr.value ? dayjs(attr.value).format('YYYY-MM-DD') : ''}
                        onChange={(e) => handleChangeValue(attr.id, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    );
                  case 'datetime':
                    return (
                      <TextField
                        disabled={saving}
                        type="datetime-local"
                        value={attr.value ? dayjs(attr.value).format('YYYY-MM-DDTHH:mm') : ''}
                        onChange={(e) => handleChangeValue(attr.id, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    );
                  case 'integer':
                  case 'decimal':
                    return (
                      <TextField
                        disabled={saving}
                        type="number"
                        value={attr.value ?? ''}
                        onChange={(e) => handleChangeValue(attr.id, e.target.value)}
                        fullWidth
                      />
                    );
                  case 'json':
                    return (
                      <TextField
                        disabled={saving}
                        value={
                          typeof attr.value === 'object'
                            ? JSON.stringify(attr.value)
                            : attr.value ?? ''
                        }
                        onChange={(e) => handleChangeValue(attr.id, e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                      />
                    );
                  default:
                    if (attr.shareValues) {
                      return (
                        <Autocomplete
                          disabled={saving}
                          freeSolo
                          fullWidth
                          options={attributeOptions?.[attr.id] || []}
                          getOptionLabel={(option) =>
                            typeof option === 'string' ? option : option.label
                          }
                          value={attr.value ?? ''}
                          onChange={(event, newValue) =>
                            handleChangeValue(attr.id, newValue)
                          }
                          renderInput={(params) => (
                            <TextField {...params} variant="outlined"
                              onChange={(e) =>
                                handleChangeValue(attr.id, e.target.value)
                              }
                            />
                          )}
                        />
                      );
                    } else {
                      return (
                        <TextField
                          disabled={saving}
                          value={attr.value ?? ''}
                          onChange={(e) => handleChangeValue(attr.id, e.target.value)}
                          fullWidth
                        />
                      );
                    }
                }
              })()
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  if (loading) return <div>Loading attributes…</div>;
  if (error) return <div style={{ color: 'red' }}>Error loading attributes.</div>;

  // Step 1: Filter out invisible attributes
  const visibleAttributes = roleContext.hasRole(ROLES.ADMIN)
    ? attributes : attributes.filter(attr => attr.visible);

  // Step 2: Pair into rows of 2
  const pairedAttributes = [];
  for (let i = 0; i < visibleAttributes.length; i += 2) {
    pairedAttributes.push([
      visibleAttributes[i],
      visibleAttributes[i + 1] || null // null if odd number of attrs
    ]);
  }

  return (
    !!attributes?.length && <Stack rowGap={1} paddingTop={'10px'}>
      <Typography fontWeight='bold' color='text.secondary'>Additional Detail</Typography>
      <Card sx={{ padding: '12px' }}>
        <Grid container spacing={1}>
          {pairedAttributes.map((pair, idx) => (
            <Grid container item xs={12} key={idx} spacing={2}>
              {renderPropertyCell(pair[0])}
              {renderPropertyCell(pair[1])}
            </Grid>
          ))}
        </Grid>
      </Card>
    </Stack>
  );
});

export default ExtendedAttributesSection;